import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MapService } from '@ebw-monorepo/map';
import { PERMISSION_NAME, PermissionService, SplitViewService } from '@ebw-monorepo/utils';
import { MtxGrid, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import moment from 'moment';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { Subscription, debounceTime, lastValueFrom } from 'rxjs';
import { CavoRaccordo } from '../../../../models/cavo-raccordo.model';
import { BaseTableComponent } from '../base-table.component';
import { RaccordiCavoAttachmentsComponent } from '../raccordi-cavo-attachments/raccordi-cavo-attachments.component';
import { RaccordiCavoEditComponent } from '../raccordi-cavo-edit/raccordi-cavo-edit.component';
@Component({
  selector: 'ebw-monorepo-raccordi-cavo-table',
  templateUrl: './raccordi-cavo-table.component.html',
  styleUrls: ['./raccordi-cavo-table.component.scss']
})
export class RaccordiCavoTableComponent extends BaseTableComponent<CavoRaccordo> implements OnInit, OnChanges, OnDestroy {
  @ViewChild('grid') public gridTable: MtxGrid;
  @ViewChild('geometryColTemplate', { static: true }) geometryColTemplate!: TemplateRef<any>;

  // template reference to render the info column
  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate!: TemplateRef<any>;

  @Output() public fiberDetailRequested: EventEmitter<CavoRaccordo> = new EventEmitter();
  @Output() public reloadFiberRequested: EventEmitter<any> = new EventEmitter();
  @Output() public onDeleteCavo: EventEmitter<any> = new EventEmitter();

  searchFilter: FormGroup;
  userCanEdit = false;
  userCanCreate = false;
  rowSelected = [];
  public rowClassFormatter: MtxGridRowClassFormatter = {
    highlighted: (data, index) => {
      return data.highlighted;
    }
  };
  private _filterSubscribe: Subscription;

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private _dialog: MatDialog,
    private _fb: FormBuilder,
    private mapService: MapService,
    private _splitViewService: SplitViewService,
    private _apiUrlService: ApiUrlService,
    private _permissionService: PermissionService
  ) {
    super();
    this._exportFilename = 'export-cavi';
  }

  // --------------------------------------------------------------------------------------------------------------------

  override ngOnInit(): void {
    super.ngOnInit();

    this.userCanEdit = this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_EDIT_WEB);
    this.userCanCreate = this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_CREATE);

    this._exportApi = {
      csv: this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV),
      html: this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.HTML)
    };

    this.initFilterForm();
  }

  // ------------------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this._filterSubscribe.unsubscribe();
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this.mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // ------------------------------------------------------------------------------------------------------------

  initializeTable(fromTabChange = false) {
    this.columnsGrid = [
      {
        header: '',
        field: 'expand',
        pinned: 'left',
        class: 'px-1',
        showExpand: true
      },
      {
        header: '',
        field: 'longitudine',
        pinned: 'left',
        class: 'px-1',
        cellTemplate: this.geometryColTemplate
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.nome_cavo'),
        field: 'nome_cavo',
        sortable: true,
        pinned: 'left'
      },
      {
        header: 'Id Gis',
        field: 'id_gis',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.data_verbale'),
        field: 'data_verbale',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.td_raccordo'),
        field: 'td_raccordo',
        sortable: false
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.sala_1'),
        field: 'sala_1',
        sortable: false
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.sala_2'),
        field: 'sala_2',
        sortable: false
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.potenzialita_cavo'),
        field: 'potenzialita_cavo',
        sortable: false
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.potenzialita_iru'),
        field: 'potenzialita_iru',
        sortable: false
      },
      {
        header: 'Azioni',
        field: 'info',
        cellTemplate: this.actionsTemplate,
        class: 'px-1',
        pinned: 'right'
      }
    ];
  }

  // ------------------------------------------------------------------------------------------------------------

  clearDefaultSearchFilter() {
    this.searchFilter.reset();
    this.fetchData();
  }

  // ------------------------------------------------------------------------------------------------------------

  getFilters(limit = this.pageSize, offset = this.offset) {
    let nextFilters = {
      limit,
      offset
    };

    if (this._currentSort) {
      nextFilters['order_by'] = this._currentSort.field;
      nextFilters['order'] = this._currentSort.direction;
    }

    //Pagination is always applied
    let filters = this.config.filters;

    //Apply filter
    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          return;
        }
        nextFilters[key] = filters[key];
      });
    }

    return nextFilters;
  }

  // ------------------------------------------------------------------------------------------------------------

  openRaccordoCavoModal(id = null) {
    let ref = this._dialog.open(RaccordiCavoEditComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '70%',
      data: {
        id
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this.fetchData();
        this.reloadFiberRequested.emit();
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  openRaccordoAttachmentsModal(id = null) {
    this._dialog.open(RaccordiCavoAttachmentsComponent, {
      maxWidth: '1200px',
      maxHeight: '100%',
      width: '70%',
      data: {
        id
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  openFiberDetail(raccordo) {
    this.resetHightlightedItem();
    raccordo.highlighted = true;
    this.fiberDetailRequested.emit(raccordo);
  }
  // ------------------------------------------------------------------------------------------------------------

  fetchData(customFilter?: Record<string, any>) {
    this.resetComponentProperties();
    const filters = { ...this.getFilters(), ...customFilter };
    const url = this._apiUrlService.createUrl(API_URLS.FIBER_JOINTS.JOINTS.LIST);

    this._utilsService.postRequest(url, filters, false, {}, 'application/json').subscribe({
      next: (resp: any) => {
        this.totalCount = resp.numRows;
        this.dataGrid = resp.records;
      },
      error: (err) => {
        const title = this._translateService.instant('common.error');
        const content = err.error.message;
        this._ebwModal.openErrorModal({ title, content });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  formatExportData(data: any[], excel: boolean = false) {
    const formattedData = [];
    const fields = [
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.building_name'),
        field: 'nome_edificio'
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.building_alias'),
        field: 'alias'
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.gat_sede'),
        field: 'gat_sede'
      },
      ...this.columnsGrid,
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.fattibilita'),
        field: 'codice_fattibilita'
      },
      {
        header: 'ID Gis',
        field: 'id_gis'
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.terminazione_1'),
        field: 'terminazione_1'
      },
      {
        header: this._translateService.instant('map.sidebar.raccordo-cavo.table.terminazione_2'),
        field: 'terminazione_2'
      }
    ];

    for (const row of data) {
      const currentRow = {};
      for (const col of fields) {
        if (typeof row[col.field] === 'object' && !Array.isArray(row[col.field]) && row[col.field] !== null) {
          currentRow[<string>col.header] = `${row[col.field].latitude} ${row[col.field].longitude}`;
          continue;
        }

        if (!row[col.field]) {
          currentRow[<string>col.header] = '';
          continue;
        }

        currentRow[<string>col.header] = row[col.field];
      }
      formattedData.push(currentRow);
    }

    if (excel) {
      return {
        query: formattedData
      };
    }

    return formattedData;
  }

  // ------------------------------------------------------------------------------------------------------------

  initFilterForm() {
    this.searchFilter = this._fb.group({
      term: [''],
      data_verbale: [null]
    });

    this._filterSubscribe = this.searchFilter.valueChanges.pipe(debounceTime(300)).subscribe((val) => {
      let newFilter = {};

      if (!!val.term) {
        newFilter = {
          // codice_fattibilita: val.term,
          // td_raccordo: val.term,
          nome_cavo: val.term
          // id_gis: val.term,
          // sala_1: val.term,
          // sala_2: val.term,
          // terminazione_1: val.term,
          // terminazione_2: val.term
          // potenzialitaCavo: val.term,
          // potenzialitaIru: val.term
        };
      }

      if (val.data_verbale) {
        newFilter['data_verbale'] = moment(val.data_verbale).format('DD/MM/YYYY');
      }

      this.currentPageIndex = 0;
      this.offset = this.currentPageIndex * this.pageSize;

      this.fetchData(newFilter);
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  public onCenterMapPlaceRequested(event: MouseEvent, row: CavoRaccordo) {
    if (!row.latitudine && !row.longitudine) {
      return;
    }
    event.stopPropagation();
    this.centerMapPlace(row.latitudine, row.longitudine);
  }

  // ------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param element singolo elemento della tabella
   * @param forced parametro che indica se interrompere forzatamente la procedura dopo 1 ciclo
   * @returns void
   */
  async centerMapPlace(lat: number, lon: number) {
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this._updateSplitViewConf({ halfScreen: true, collapsed: false });

    const centerCoords = fromLonLat([lon, lat]);

    const pointFeature = new Feature({
      geometry: new Point(centerCoords)
    });

    // Se serve, converti in GeoJSON
    const geojsonFormat = new GeoJSON();
    const geojsonGeom = geojsonFormat.writeGeometryObject(pointFeature.getGeometry());
    this.centerMap(geojsonGeom);
    return;
  }

  // ------------------------------------------------------------------------------------------------------------

  centerMap(geometry: any) {
    this.mapService.addFeatureToTemporaryLayer(geometry, null, false, false);
    setTimeout(() => {
      this.mapService.centerMapOnGeometry(geometry, false, true);
    }, 0);
  }

  // ------------------------------------------------------------------------------------------------------------

  resetHightlightedItem() {
    setTimeout(() => {
      this.dataGrid.map((_: any) => (_.highlighted = false));
    }, 0);
  }

  // ------------------------------------------------------------------------------------------------------------

  private _updateSplitViewConf(layoutConf) {
    let currentLayoutConf = this._splitViewService.viewLayountConfig$.value;
    if (layoutConf && currentLayoutConf.isEnable) {
      currentLayoutConf = Object.assign({}, currentLayoutConf, layoutConf);
      this._splitViewService.layoutConfChange(currentLayoutConf);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  deleteCavo(id: number, force = false) {
    const url = `${this._apiUrlService.createUrl(API_URLS.FIBER_JOINTS.JOINTS.BASE)}/${id}`;
    return this._utilsService.deleteRequest(url, { force }, false, {}, 'application/json');
  }

  // ------------------------------------------------------------------------------------------------------------

  async onDeleteCavoVirtuale(id: number): Promise<void> {
    const title = 'Conferma eliminazione';
    const content = 'Sei sicuro di voler eliminare questo cavo? Verranno eliminate anche fibre e allegati collegati.';
    const modalOpening = this._ebwModal.openConfirmModal({ title, content }).afterClosed();
    const confirm = await lastValueFrom(modalOpening);
    if (!confirm) return;

    try {
      const request = this.deleteCavo(id, false);
      const response = await lastValueFrom(request);

      if (!response.deleted) {
        const deleteConfirmed = await this.askForHardDeleteCavoVirtuale(response.notOpenFibers);
        if (!deleteConfirmed) return;

        const hardRequest = this.deleteCavo(id, true);
        await lastValueFrom(hardRequest);
      }

      const title = this._translateService.instant('common.delete_success');
      this._ebwModal.openSuccessModal({ title, content: '' });

      this.onDeleteCavo.emit({ id });
      this.fetchData();
    } catch (error) {
      console.error('[RaccordiCavoTableComponent][deleteCavoVirtuale] ', error);
      const title = this._translateService.instant('common.error');
      const content = error.error.message;
      this._ebwModal.openErrorModal({ title, content });
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  async askForHardDeleteCavoVirtuale(fibreNonLibere = 0) {
    if (!fibreNonLibere) {
      return;
    }
    const title = 'Conferma eliminazione';
    const content = `In questo cavo sono presenti ${fibreNonLibere} fibre non libere, vuoi procedere ugualmente?`;
    const modalOpening = this._ebwModal.openConfirmModal({ title, content }).afterClosed();
    const confirm = await lastValueFrom(modalOpening);
    return confirm;
  }

  // ------------------------------------------------------------------------------------------------------------
}
